/**
 * Tema customizado do Chakra UI para o Sistema MEDICARE
 */

import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
import { colors } from './colors';
import { fonts } from './fonts';
import { components } from './components';
import { styles } from './styles';

// Configuração do modo de cor
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

// Breakpoints responsivos
const breakpoints = {
  base: '0em',    // 0px
  sm: '30em',     // 480px
  md: '48em',     // 768px
  lg: '62em',     // 992px
  xl: '80em',     // 1280px
  '2xl': '96em',  // 1536px
};

// Espaçamentos customizados
const space = {
  px: '1px',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  28: '7rem',
  32: '8rem',
  36: '9rem',
  40: '10rem',
  44: '11rem',
  48: '12rem',
  52: '13rem',
  56: '14rem',
  60: '15rem',
  64: '16rem',
  72: '18rem',
  80: '20rem',
  96: '24rem',
};

// Sombras customizadas
const shadows = {
  xs: '0 0 0 1px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  outline: '0 0 0 3px rgba(66, 153, 225, 0.6)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  none: 'none',
  // Sombras específicas do MEDICARE
  card: '0 4px 12px rgba(0, 0, 0, 0.1)',
  cardHover: '0 8px 25px rgba(0, 0, 0, 0.15)',
  modal: '0 20px 40px rgba(0, 0, 0, 0.2)',
};

// Raios de borda
const radii = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
};

// Tema principal
export const theme = extendTheme({
  config,
  colors,
  fonts,
  components,
  styles,
  breakpoints,
  space,
  shadows,
  radii,
  // Configurações adicionais
  semanticTokens: {
    colors: {
      // Cores semânticas para diferentes estados
      'chakra-body-text': {
        _light: 'gray.800',
        _dark: 'whiteAlpha.900',
      },
      'chakra-body-bg': {
        _light: 'white',
        _dark: 'gray.800',
      },
      'chakra-border-color': {
        _light: 'gray.200',
        _dark: 'whiteAlpha.300',
      },
      'chakra-subtle-bg': {
        _light: 'gray.100',
        _dark: 'gray.700',
      },
      'chakra-subtle-text': {
        _light: 'gray.600',
        _dark: 'gray.400',
      },
    },
  },
  // Transições globais
  transition: {
    property: {
      common: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
      colors: 'background-color, border-color, color, fill, stroke',
      dimensions: 'width, height',
      position: 'left, right, top, bottom',
      background: 'background-color, background-image, background-position',
    },
    easing: {
      'ease-in': 'cubic-bezier(0.4, 0, 1, 1)',
      'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
      'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    duration: {
      'ultra-fast': '50ms',
      faster: '100ms',
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
      slower: '500ms',
      'ultra-slow': '1000ms',
    },
  },
});